import {
  TaskCreate_FormSections,
  FNOL_RequestForm,
  FNOL_RequestFormDefaultValues,
  FNOL_RequestFormSchema,
  FNOL_TaskTypes,
  IngressRequestDefaultValues,
  IngressRequestForm,
  MultiSelectOption,
  ResultStatus,
  AttributeOptions,
  AvailableSlots,
  IngressRequestFormSchema,
  FNOL_ClaimOwnerOrComplexAvailabilityResponse,
} from '@dispatch/shared/types';
import { DrawerComponent, Form, RenderFuncProps } from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { Control, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IsForcedSection } from './isForcedSection';
import { Button, Divider, Stack } from '@mui/material';
import { FNOLRequestSection } from './FNOLRequestSection';
import { IngressRequestSection } from './ingressRequestSection';
import { sub } from 'date-fns';
import { isNull } from 'lodash';
import { IngressRequestReviewSection } from './ingressRequestReviewSection';
import { utcToZonedTime } from 'date-fns-tz';
import { FNOLAvailabilitySectionList } from './FNOLAvailabilitySectionList';

export type TaskCreateProps = {
  navigateToOnClose?: string;
  formSection: TaskCreate_FormSections;
  setFormSection: React.Dispatch<React.SetStateAction<TaskCreate_FormSections>>;
  setResourceInput: React.Dispatch<React.SetStateAction<string>>;
  activeResources?: MultiSelectOption[];
  fnolStatus: ResultStatus;
  selectedSlot: number | null;
  setSelectedSlot: React.Dispatch<React.SetStateAction<number | null>>;
  isForced: boolean;
  setIsForced: React.Dispatch<React.SetStateAction<boolean>>;
  companyAttributes: AttributeOptions[];
  onHandleFNOLRequestFormSubmit: (values: FNOL_RequestForm) => void;
  availableSlots:
    | AvailableSlots[]
    | FNOL_ClaimOwnerOrComplexAvailabilityResponse;
  onHandleIngressRequestFormSubmit: (values: IngressRequestForm) => void;
  ingressStatus: ResultStatus;
  saveButtonLabel: string;
  setSaveButtonLabel: React.Dispatch<React.SetStateAction<string>>;
  timeZone?: string;
};

export function TaskCreate({
  navigateToOnClose = '/tasks',
  formSection,
  setFormSection,
  setResourceInput,
  activeResources,
  fnolStatus,
  selectedSlot,
  setSelectedSlot,
  isForced,
  setIsForced,
  companyAttributes,
  onHandleFNOLRequestFormSubmit,
  availableSlots,
  onHandleIngressRequestFormSubmit,
  ingressStatus,
  saveButtonLabel,
  setSaveButtonLabel,
  timeZone = '',
}: TaskCreateProps) {
  const navigate = useNavigate();

  const renderBackButton = () => {
    return (
      <Stack>
        <Button
          type="button"
          variant="outlined"
          onClick={FNOL_FormSectionPrevious}
          disabled={formSection === TaskCreate_FormSections.INITIAL}
          sx={{
            fontWeight: '700',
            lineHeight: '1.3750em',
            letterSpacing: '0.029em',
          }}
        >
          Back
        </Button>
      </Stack>
    );
  };

  const renderIsForcedSection = () => {
    return <IsForcedSection isForced={isForced} setIsForced={setIsForced} />;
  };

  const renderFormHelper = () => {
    return (
      <>
        <Stack>{renderIsForcedSection()}</Stack>
        <Divider style={{ marginTop: 20, marginBottom: 20 }} />
        <Stack>
          {!isForced && formSection === TaskCreate_FormSections.INITIAL && (
            <Form<FNOL_RequestForm>
              formId="fnolRequestForm"
              errors={fnolRequestFormState.errors}
              control={fnolRequestFormControl}
              handleSubmit={fnolRequestHandleSubmit}
              render={({ control, errors }: RenderFuncProps) => {
                return (
                  <div style={{ width: '100%' }}>
                    {renderFNOLRequestFormSection(control, errors)}
                  </div>
                );
              }}
            />
          )}
          {isForced && formSection === TaskCreate_FormSections.INITIAL && (
            <Form<IngressRequestForm>
              formId="ingressRequestForm"
              errors={ingressRequestFormState.errors}
              control={ingressRequestFromControl}
              handleSubmit={ingressRequestHandleSubmit}
              render={({ control, errors }: RenderFuncProps) => {
                return (
                  <div style={{ width: '100%' }}>
                    {renderIngressSection(control, errors)}
                  </div>
                );
              }}
            />
          )}
        </Stack>
      </>
    );
  };

  const {
    control: fnolRequestFormControl,
    formState: fnolRequestFormState,
    handleSubmit: fnolRequestHandleSubmit,
    getValues: fnolRequestFormGetValues,
    setValue: fnolRequestFormSetValue,
    watch: fnolRequestFormWatch,
  } = useForm<FNOL_RequestForm>({
    resolver: yupResolver(FNOL_RequestFormSchema),
    defaultValues: FNOL_RequestFormDefaultValues,
  });

  const renderFNOLRequestFormSection = (
    control: Control<FieldValues>,
    errors: FieldErrors
  ) => {
    return (
      <FNOLRequestSection
        control={control}
        errors={errors}
        activeResources={activeResources}
        setResourceInput={setResourceInput}
        companyAttributes={companyAttributes}
        watch={fnolRequestFormWatch}
        getValues={fnolRequestFormGetValues}
        setValue={fnolRequestFormSetValue}
        setSelectedSlot={setSelectedSlot}
      />
    );
  };

  const renderFNOLAvailableSlotsSection = () => {
    return (
      <FNOLAvailabilitySectionList
        taskType={fnolRequestFormGetValues('fnolTaskType')}
        availableSlots={availableSlots}
        status={fnolStatus}
        setSelectedSlot={setSelectedSlot}
        timeZone={timeZone}
        getValues={fnolRequestFormGetValues}
      />
    );
  };

  const {
    control: ingressRequestFromControl,
    formState: ingressRequestFormState,
    handleSubmit: ingressRequestHandleSubmit,
    reset: ingressRequestReset,
    getValues: ingressRequestGetValues,
    setValue: ingressRequestFormSetValue,
    watch: ingressRequestFormWatch,
  } = useForm<IngressRequestForm>({
    defaultValues: IngressRequestDefaultValues,
    resolver: yupResolver(IngressRequestFormSchema),
  });

  useEffect(() => {
    ingressRequestFormSetValue('claim.isForced', isForced);
  }, [ingressRequestFormSetValue, isForced]);

  useEffect(() => {
    if (
      ingressRequestGetValues('assignmentHandling.methodOfInspection') ===
      'FIELD'
    ) {
      ingressRequestFormSetValue('assignmentHandling.travel', true);
    }
  }, [
    ingressRequestFormSetValue,
    ingressRequestGetValues('assignmentHandling.methodOfInspection'),
  ]);

  const renderIngressSection = (
    control: Control<FieldValues>,
    errors: FieldErrors
  ) => {
    return (
      <IngressRequestSection
        control={control}
        errors={errors}
        isForced={isForced}
        activeResources={activeResources}
        setResourceInput={setResourceInput}
        getValues={ingressRequestGetValues}
        watch={ingressRequestFormWatch}
        companyAttributes={companyAttributes}
      />
    );
  };

  const renderIngressRequestReviewSection = () => {
    return (
      <IngressRequestReviewSection
        getValues={ingressRequestGetValues}
        timeZone={timeZone}
      />
    );
  };

  const fnolValues = fnolRequestFormGetValues();
  const ingressValues = ingressRequestGetValues();
  const isNonComplex = fnolValues?.fnolTaskType === FNOL_TaskTypes.NON_COMPLEX;

  const copyFnolToIngress = () => {
    const mustEndBefore = isNonComplex
      ? (availableSlots as AvailableSlots[])?.[selectedSlot || 0]
          .projectedWorkEndTime
      : (availableSlots as FNOL_ClaimOwnerOrComplexAvailabilityResponse)
          .projectedWorkStartTime;

    const mustStartAfter = isNonComplex
      ? (availableSlots as AvailableSlots[])?.[selectedSlot || 0]
          .projectedWorkStartTime
      : (availableSlots as FNOL_ClaimOwnerOrComplexAvailabilityResponse)
          .projectedWorkStartTime;

    const newIngressValues: IngressRequestForm = {
      claim: {
        lossLocation: fnolValues.lossLocation,
        claimNumber: ingressValues.claim.claimNumber || '',
        insuredName: ingressValues.claim.insuredName || '',
        dateOfLoss: sub(new Date(), {
          days: 5,
        }).toISOString(),
        dateOfNotice: new Date().toISOString(),
        propertyType: ingressValues.claim.propertyType,
        peril: fnolValues.peril,
        catCode: fnolValues.catCode,
      },
      contact: {
        primary: ingressValues.contact.primary,
        hasAlternateLocation: ingressValues.contact.hasAlternateLocation,
        hasSecondaryContact: ingressValues.contact.hasSecondaryContact,
      },
      assignmentHandling: {
        travel:
          isForced || fnolValues.methodOfInspection === 'VIRTUAL'
            ? false
            : true,
        templateId: ingressValues.assignmentHandling.templateId || '',
        durationInMinutes: fnolValues.durationInMinutes,
        priority: ingressValues.assignmentHandling.priority,
        severity: fnolValues.severity,
        methodOfInspection: fnolValues.methodOfInspection,
        mustEndBefore: utcToZonedTime(mustEndBefore, timeZone).toISOString(),
        mustStartAfter: utcToZonedTime(mustStartAfter, timeZone).toISOString(),
        notes: ingressValues.assignmentHandling.notes,
        preferredLanguage: fnolValues.preferredLanguage,
        assignmentCategory: fnolValues.assignmentCategory,
        taskType: fnolValues.fnolTaskType,
      },
      claimAttributes: fnolValues.claimAttributes,
    };

    ingressRequestReset(newIngressValues);
  };

  const FNOL_FormSectionProgressor = () => {
    switch (formSection) {
      case TaskCreate_FormSections.INITIAL:
        // add a check here to see if the form is forced
        // if forced go to Ingress Section
        if (isForced) {
          ingressRequestHandleSubmit(onHandleIngressRequestFormSubmit)();
          break;
        }

        fnolRequestHandleSubmit(onHandleFNOLRequestFormSubmit)();

        break;
      case TaskCreate_FormSections.AVAILABLE_SLOTS:
        copyFnolToIngress();
        // copy to ingress section
        setFormSection(TaskCreate_FormSections.INGRESS);
        setSaveButtonLabel('Review');
        break;
      case TaskCreate_FormSections.INGRESS:
        ingressRequestHandleSubmit(onHandleIngressRequestFormSubmit)();

        break;
      case TaskCreate_FormSections.REVIEW:
        // ingress
        ingressRequestHandleSubmit(onHandleIngressRequestFormSubmit)();

        break;
      default:
        break;
    }
  };

  const FNOL_FormSectionPrevious = () => {
    switch (formSection) {
      case TaskCreate_FormSections.REVIEW:
        // add a check here to see if the form was forced
        // if forced, go back to isForced Section

        if (isForced) {
          setFormSection(TaskCreate_FormSections.INITIAL);
          setSaveButtonLabel('Next');
          break;
        }

        setSaveButtonLabel('Review');
        setFormSection(TaskCreate_FormSections.INGRESS);
        break;
      case TaskCreate_FormSections.INGRESS:
        setSaveButtonLabel('Next');
        setFormSection(TaskCreate_FormSections.AVAILABLE_SLOTS);
        break;
      case TaskCreate_FormSections.AVAILABLE_SLOTS:
        setSaveButtonLabel('Next');
        setFormSection(TaskCreate_FormSections.INITIAL);
        break;
      default:
        break;
    }
  };

  const disableSaveButton = () => {
    return (
      (formSection === TaskCreate_FormSections.AVAILABLE_SLOTS &&
        isNull(selectedSlot)) ||
      (!fnolRequestFormState.isDirty &&
        formSection === TaskCreate_FormSections.INITIAL &&
        !isForced) ||
      (!ingressRequestFormState.isDirty &&
        formSection === TaskCreate_FormSections.INGRESS) ||
      (!ingressRequestFormState.isDirty &&
        formSection === TaskCreate_FormSections.INITIAL &&
        isForced)
    );
  };

  return (
    <DrawerComponent
      onClose={() => {
        navigate(navigateToOnClose);
      }}
      onSave={FNOL_FormSectionProgressor}
      saveButtonLabel={saveButtonLabel}
      renderLeftSideButtons={[renderBackButton()]}
      showButtons
      saveDisabled={disableSaveButton()}
      status={fnolStatus || ingressStatus}
      sx={{
        width: '65vw',
      }}
    >
      {formSection === TaskCreate_FormSections.INITIAL && renderFormHelper()}
      {formSection === TaskCreate_FormSections.AVAILABLE_SLOTS &&
        renderFNOLAvailableSlotsSection()}
      {formSection === TaskCreate_FormSections.INGRESS && (
        <Form<IngressRequestForm>
          formId="ingressRequestForm"
          errors={ingressRequestFormState.errors}
          control={ingressRequestFromControl}
          handleSubmit={ingressRequestHandleSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <div style={{ width: '100%' }}>
                {renderIngressSection(control, errors)}
              </div>
            );
          }}
        />
      )}
      {formSection === TaskCreate_FormSections.REVIEW &&
        renderIngressRequestReviewSection()}
    </DrawerComponent>
  );
}
